import React from 'react';
import { To, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  CREATE_DASHBOARD,
  dashboardAddAnalysesMutation,
} from '../../../../utils/apollo/dashboards';
import KubitModal from '../../../KubitModal';
import {
  IDashboardAddAnalysesMutation,
  IDashboardAddAnalysesMutationVariables,
  IDashboardCreateMutation,
  IDashboardCreateMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { appRoutes } from '../../../../path';
import { useAppDispatch } from '../../../../redux/hooks';
import toastMessage from '../../../../utils/toastMessage';
import { closeModal } from '../../../../redux/slices/modal';
import {
  dashboardCreated,
  dashboardUpdated,
} from '../../../../utils/analytics/events/dashboard/events';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import useCachedEntitiesSelector from '../../../../redux/selectorHooks/useCachedEntitiesSelector';
import { NEW_DASHBOARD_SETTINGS } from './utils';
import { IAddReportToDashboardFormState } from './types';
import AddReportToDashboardForm from './AddReportToDashboardForm';
import { clearCreateNewReport } from '../../../../redux/slices/cachedEntities';
import { usePrepareChartPrefs } from '../../hooks/useAnalysisSetChartPref';
import { useChartContext } from '../../ChartContext';

interface IProps {
  analysisId: string;
}

const AddReportToDashboardModal = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const {
    metadata: { display, description },
  } = useChartContext();
  const createNewReport = useCachedEntitiesSelector('createNewReport');
  const preparePrefInput = usePrepareChartPrefs(analysisId);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const onDashboardCreate = ({ dashboardCreate }: IDashboardCreateMutation) => {
    const { entity, errors } = dashboardCreate;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(entity);
    track(dashboardCreated(entity));
    handleClose();

    if (createNewReport) {
      dispatch(clearCreateNewReport());
    }
  };

  const onAddToDashboard = ({
    dashboardAddAnalyses,
  }: IDashboardAddAnalysesMutation) => {
    const { entity, errors } = dashboardAddAnalyses;
    if (printGraphqlErrors(errors)) {
      return;
    }
    assertNotNil(entity);
    track(dashboardUpdated(entity));
    handleClose();

    if (createNewReport) {
      dispatch(clearCreateNewReport());
    }
  };

  const [executeCreateDashboard] = useMutation<
    IDashboardCreateMutation,
    IDashboardCreateMutationVariables
  >(CREATE_DASHBOARD, {
    onCompleted: onDashboardCreate,
  });

  const [executeDashboardAddAnalyses] = useMutation<
    IDashboardAddAnalysesMutation,
    IDashboardAddAnalysesMutationVariables
  >(dashboardAddAnalysesMutation, {
    onCompleted: onAddToDashboard,
  });

  const handleSubmit = async (
    formData: IAddReportToDashboardFormState,
    shouldRedirect: boolean,
  ) => {
    const { name, description, baseId } = formData;
    const { pref } = preparePrefInput();
    const analysisDetails = {
      analysisId,
      name,
      description,
      pref,
    };

    if (baseId === NEW_DASHBOARD_SETTINGS.key) {
      const result = await executeCreateDashboard({
        variables: {
          input: {
            name: NEW_DASHBOARD_SETTINGS.menuLabel,
            description: '', // user will add description after redirect
            analyses: [analysisDetails],
          },
        },
      });

      const entity = result?.data?.dashboardCreate?.entity;

      if (entity) {
        const redirectUrl = appRoutes.dashboard.generateLink(entity.baseId);
        toastMessage({
          message: `Dashboard ${entity.name} has been created.`,
          type: shouldRedirect ? 'info' : 'action',
          redirectPath: shouldRedirect ? undefined : redirectUrl,
        });

        if (shouldRedirect) {
          navigate(redirectUrl);
        }
      }
    } else {
      const result = await executeDashboardAddAnalyses({
        variables: {
          input: {
            baseId,
            analyses: [analysisDetails],
          },
        },
      });

      const entity = result?.data?.dashboardAddAnalyses?.entity;

      if (entity) {
        const redirectUrl: To = appRoutes.dashboard.generateLink(entity.baseId);
        toastMessage({
          message: `Report added to dashboard ${entity.name}.`,
          type: shouldRedirect ? 'info' : 'action',
          redirectPath: shouldRedirect ? undefined : redirectUrl,
        });

        if (shouldRedirect) {
          navigate(redirectUrl);
        }
      }
    }
  };

  return (
    <KubitModal header={'Add to Dashboard'} onClose={handleClose} open>
      <AddReportToDashboardForm
        defaultValues={{
          name: display,
          description,
          baseId: createNewReport?.dashboardBaseId ?? '',
        }}
        onSubmit={handleSubmit}
        onReset={handleClose}
      />
    </KubitModal>
  );
};

export default AddReportToDashboardModal;
